/**
 * Whether the user last left the sidebar expanded or collapsed, kept in localStorage so a reload opens it
 * the same way. Only the user's own toggle on a desktop-width window writes it: the off-canvas drawer under
 * 768px, the guided tour and the viewport breakpoints never do. Once written it wins over the breakpoint
 * default in both designs, at desktop widths only: under 768px the drawer always starts closed. Storage can
 * be absent (private mode, blocked site data): every read and write is guarded, and with nothing stored the
 * sidebar follows the viewport.
 */

const STORAGE_KEY = 'sidebar-expanded';

/** `useIsMobile`'s breakpoint, read once outside React: under it the sidebar is the off-canvas drawer. */
const OFF_CANVAS_QUERY = '(max-width: 767.95px)';

/** The user's last choice, or `null` when they never toggled the sidebar or the viewport is mobile. */
export function readSidebarExpanded(): boolean | null {
    if (window.matchMedia(OFF_CANVAS_QUERY).matches) return null;
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === 'true') return true;
        if (stored === 'false') return false;
        return null;
    } catch {
        return null;
    }
}

/** Records the user's toggle, for the next load. */
export function rememberSidebarExpanded(expanded: boolean): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, String(expanded));
    } catch {
        // Storage unavailable: the next load falls back to the viewport default.
    }
}
